import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import type { ApiError, AuthResponse } from './types'

const ACCESS_TOKEN_KEY = 'sems.accessToken'
const REFRESH_TOKEN_KEY = 'sems.refreshToken'

/**
 * Token storage.
 *
 * Phase 6 keeps both tokens in localStorage so the app works against the API as it is
 * today. The target design (docs/01-system-architecture.md section 5) delivers the refresh
 * token in an HttpOnly, Secure, SameSite=Strict cookie so that a cross-site scripting bug
 * cannot read it. That needs a cookie mode on the backend's /auth endpoints, which is
 * tracked as a Phase 12 hardening item and should be done before this ships publicly.
 */
export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, access)
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

/** Called when the session cannot be recovered, so the app can route back to login. */
let onSessionExpired: (() => void) | null = null
export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler
}

// A single in-flight refresh, shared by every request that hits a 401 at the same time.
// Without this, five parallel list calls would each try to rotate the refresh token, and
// four of them would be treated by the server as token reuse and kill the session.
let refreshInFlight: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStore.getRefresh()
  if (!refreshToken) {
    throw new Error('No refresh token')
  }
  const response = await axios.post<AuthResponse>(
    '/api/v1/auth/refresh',
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  )
  tokenStore.set(response.data.accessToken, response.data.refreshToken)
  return response.data.accessToken
}

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as RetriableConfig | undefined
    const code = error.response?.data?.code
    const status = error.response?.status

    const isAuthCall = original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh')
    const canRetry =
      status === 401 &&
      !isAuthCall &&
      original &&
      !original._retried &&
      code !== 'REFRESH_REUSE_DETECTED'

    if (canRetry) {
      original._retried = true
      try {
        refreshInFlight = refreshInFlight ?? refreshAccessToken().finally(() => {
          refreshInFlight = null
        })
        const token = await refreshInFlight
        original.headers = { ...original.headers, Authorization: `Bearer ${token}` }
        return api.request(original)
      } catch {
        tokenStore.clear()
        onSessionExpired?.()
        return Promise.reject(error)
      }
    }

    if (status === 401 && !isAuthCall) {
      tokenStore.clear()
      onSessionExpired?.()
    }
    return Promise.reject(error)
  },
)

/** Turns any thrown value into something worth showing a site engineer. */
export function describeError(error: unknown): string {
  if (axios.isAxiosError<ApiError>(error)) {
    const data = error.response?.data
    if (data?.errors?.length) {
      return data.errors.map((e) => `${humanField(e.field)}: ${e.message}`).join('\n')
    }
    if (data?.detail) return data.detail
    if (data?.title) return data.title
    if (error.code === 'ECONNABORTED') return 'The server took too long to respond.'
    if (!error.response) return 'Cannot reach the server. Is the backend running?'
    return `${error.response.status} ${error.response.statusText}`
  }
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

export function errorCode(error: unknown): string | undefined {
  return axios.isAxiosError<ApiError>(error) ? error.response?.data?.code : undefined
}

function humanField(field: string): string {
  return field
    .replace(/\[(\d+)\]/g, ' $1')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[._]/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())
}
